import React, { MouseEvent, useState } from 'react';

import AlertDialog from '../../shared/components/AlertDialog';
import AuthService from '../../shared/services/AuthService';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CertIcon from './CertIcon';
import CertService from '../../report/services/CertService';
import CertStatus from '../../shared/models/CertificateStatus';
import { Divider } from '@mui/material';
import FileService from '../../shared/services/FileService';
import Grid from '@mui/material/Unstable_Grid2';
import LessonService from '../services/LessonService';
import Overlay from '../../shared/components/Overlay';
import ProgressStatusCode from '../models/ProgressStatusCode';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import styles from '../styles/CertificateCard.module.css';

interface CertificateStatus {
  status: CertStatus;
  color: string;
}

const getCertStatus = (statusCode: ProgressStatusCode): CertificateStatus => {
  switch (statusCode) {
    case 'C':
      return { status: 'certified', color: '#9EC751' };
    case 'X':
      return { status: 'expiring', color: '#F7AA18' };
    case 'E':
      return { status: 'expired', color: '#F74B18' };
    default:
      return null;
  }
};

interface CertificateCardProps {
  completed: string;
  title: string;
  isLesson?: boolean;
  certStatus: ProgressStatusCode;
  lessonOrCoursekey: number;
}
export default function CertificateCard(props: CertificateCardProps) {
  const label = props.isLesson ? 'Lesson' : 'Course';
  const userkey = AuthService.Userkey;

  const [fetchingCert, setFetchingCert] = useState(false);
  const [fetchCertError, setFetchCertError] = useState(false);

  const certStatus = getCertStatus(props.certStatus);

  const handleCertIconClick = () => {
    setFetchingCert(true);
    const promise = props.isLesson
      ? CertService.getModuleCertificate(userkey, props.lessonOrCoursekey)
      : CertService.getCourseCertificate(userkey, props.lessonOrCoursekey);
    promise
      .then((data) => {
        setFetchingCert(false);
        FileService.open(data);
      })
      .catch(() => {
        setFetchingCert(false);
        setFetchCertError(true);
      });
  };

  return (
    <>
      <Grid
        container
        justifyContent="space-between"
        wrap="nowrap"
        alignItems="center"
        className={styles.achievedDate}
      >
        <Grid>
          <Typography variant="body2" className={styles.typography}>
            {`Achieved: ${dayjs(props.completed).format('D MMM YYYY h:mm A')}`}
          </Typography>
        </Grid>
        {props.certStatus && (
          <Grid
            style={{ color: certStatus.color }}
            className={styles.uppercase}
          >
            <Typography variant="body2" className={styles.typography}>
              {certStatus.status}
            </Typography>
          </Grid>
        )}
      </Grid>
      <Grid className={styles.cardContainer}>
        <Card
          elevation={1}
          sx={
            !props.isLesson
              ? (theme) => ({
                  outline: '2px solid',
                  outlineColor: theme.palette.primary.main
                })
              : undefined
          }
        >
          <CardContent style={{ padding: 8 }}>
            <Grid container alignItems="center" spacing={2}>
              <Grid>
                <CertIcon
                  status={LessonService.toCertStatus(props.certStatus, true)}
                  onClick={(e: MouseEvent<HTMLDivElement>) => {
                    e.stopPropagation();
                    handleCertIconClick();
                  }}
                />
              </Grid>
              <Grid className={styles.title}>
                <Typography variant="body1" noWrap={true}>
                  {props.title}
                </Typography>
              </Grid>
              <Divider
                className={styles.divider}
                orientation="vertical"
                flexItem
              />
              <Grid className={styles.cardContent}>
                <Typography variant="body1" className={styles.uppercase}>
                  {`${label} certificate`}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
      {fetchingCert && <Overlay spinner message="Downloading certificate..." />}
      {fetchCertError && (
        <AlertDialog
          title="View Certificate"
          message="Failed to download the certificate."
          onClose={() => setFetchCertError(false)}
        />
      )}
    </>
  );
}
